import { Countdown, Duration } from '@/components';
import PortalTooltip from '@/components/PortalTooltip';
import { Label } from '@/components/ui/label';
import { ProcessJob, ProcessJobType } from '@/graphql/graphql';
import { useMe } from '@/hooks/useMe';
import { cn } from '@/lib/utils';
import { CheckCircleSolid, ClockCircleSolid, DangerCircleSolid, InfoHexagonSolid } from '@mynaui/icons-react';
import { Loader2 } from 'lucide-react';
import { Fragment, FunctionComponent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface iJobListProps {
  jobs: Partial<ProcessJob>[];
}

const JobList: FunctionComponent<iJobListProps> = ({ jobs }) => {
  const { t } = useTranslation('project');

  const jobTypeMap: Record<ProcessJobType, string> = {
    DWG: t('jobTypes.DWG'),
    DXF: t('jobTypes.DXF'),
    SHP: t('jobTypes.SHP'),
    E57: t('jobTypes.E57'),
    GEOJSON: t('jobTypes.GEOJSON'),
    GEOTIFF: t('jobTypes.GEOTIFF'),
    GROUND_CLASSIFY: t('jobTypes.GROUND_CLASSIFY'),
    IMAGE: t('jobTypes.IMAGE'),
    IMAGE_COMPRESSION: t('jobTypes.IMAGE_COMPRESSION'),
    IPRJ: t('jobTypes.IPRJ'),
    LAS: t('jobTypes.LAS'),
    RCS: t('jobTypes.RCS'),
    PTS: t('jobTypes.PTS'),
    IFC: t('jobTypes.IFC'),
    IFC_FRAGMENT: t('jobTypes.IFC_FRAGMENT'),
    LANDXML: t('jobTypes.LANDXML'),
    XGRIDS: t('jobTypes.XGRIDS'),
    SPLAT: t('jobTypes.SPLAT'),
    FJD_IMAGE: t('jobTypes.FJD_IMAGE'),
    TRIMBLE_IMAGE: t('jobTypes.TRIMBLE_IMAGE'),
    TOPCON_IMAGE: t('jobTypes.TOPCON_IMAGE'),
    OMNISLAM_IMAGE: t('jobTypes.OMNISLAM_IMAGE'),
    LEICA_IMAGE: t('jobTypes.LEICA_IMAGE'),
    DJI_IMAGE: t('jobTypes.DJI_IMAGE'),
    CHC_IMAGE: t('jobTypes.CHC_IMAGE'),
    POST: t('jobTypes.POST'),
    WMS: t('jobTypes.WMS'),
    POTREE: t('jobTypes.POTREE'),
    PRIVACY: t('jobTypes.PRIVACY'),
    JP2: t('jobTypes.JP2'),
    RENDER: t('jobTypes.RENDER'),
    DECIMATED: t('jobTypes.DECIMATED'),
    CLIPBOX_EXPORT: t('jobTypes.CLIPBOX_EXPORT'),
    RAINFALL_ANALYSIS: t('jobTypes.RAINFALL_ANALYSIS'),
  };

  const statusMap = {
    WAITING: {
      label: t('jobStatus.WAITING'),
      className: 'bg-gray-500 text-white',
      icon: <ClockCircleSolid className='size-5 text-amber-500' />,
    },
    PROCESSING: {
      label: t('jobStatus.PROCESSING'),
      className: 'bg-blue-500 text-white',
      icon: <Loader2 className='size-5 animate-spin' />,
    },
    PROCESSED: {
      label: t('jobStatus.PROCESSED'),
      className: 'bg-green-500 text-white',
      icon: <CheckCircleSolid className='size-5 text-emerald-500' />,
    },
    FAILED: {
      label: t('jobStatus.FAILED'),
      className: 'bg-red-500 text-white',
      icon: <DangerCircleSolid className='size-5 text-rose-500' />,
    },
  };

  const topLevelJobs = jobs?.filter((job) => !job.parentItemId);

  if (jobs?.length === 0) return null;

  return (
    <>
      <Label className='mt-8'>{t('batch.jobs')}</Label>
      <div className='mt-2 border border-slate-200/70 rounded-md px-2 py-2 bg-white dark:bg-stone-800/70 dark:border-stone-800/70 gap-2 flex flex-col'>
        {topLevelJobs?.map((job) => {
          return <JobItem key={job.id} job={job} jobs={jobs ?? []} jobTypeMap={jobTypeMap} statusMap={statusMap} />;
        })}
      </div>
    </>
  );
};

const JobItem: FunctionComponent<{
  job: Partial<ProcessJob>;
  jobs: Partial<ProcessJob>[];
  jobTypeMap: Record<ProcessJobType, string>;
  statusMap: Record<string, { label: string; className: string; icon: ReactNode }>;
}> = ({ job, jobs, jobTypeMap, statusMap }) => {
  const { t } = useTranslation('project');
  const { me } = useMe();
  const childrenJobs = jobs?.filter((j) => j.parentItemId === job.id);

  return (
    <Fragment key={job.id}>
      <div key={job.id}>
        <div className='flex items-center gap-1.5'>
          {/* <ChevronRight className='size-4 stroke-2 text-slate-500' /> */}
          <PortalTooltip id={`job-status-${job.id}`}>{statusMap[job.status || 'WAITING'].label}</PortalTooltip>
          <div className='w-5 flex items-center justify-center [&>svg]:size-5' data-tooltip-id={`job-status-${job.id}`}>
            {statusMap[job.status || 'WAITING'].icon}
          </div>
          <span className='text-left font-medium text-sm text-slate-900 dark:text-white'>
            {jobTypeMap[job.type || 'DWG'] || job.type}

            {job.label ? <span className='opacity-60'>: {job.label}</span> : '...'}
          </span>
          <div className='text-xs text-slate-500 ml-auto dark:text-stone-400 flex items-center gap-2'>
            {me?.email.endsWith('@actegon.com') && job.logStream && (
              <InfoHexagonSolid
                className={cn(
                  'size-5 text-gray-500 fill-gray-100 dark:fill-stone-700 dark:text-stone-300 cursor-pointer',
                )}
                onClick={() => window.open(job.logStream!, '_blank')}
              />
            )}
            {job.estimatedCompletion && !job.completedAt ? (
              <Countdown estimatedCompletion={job.estimatedCompletion} startedAt={job.startedAt} />
            ) : job.startedAt ? (
              <Duration startedAt={job.startedAt} completedAt={job.completedAt} />
            ) : null}
          </div>
        </div>
      </div>
      {childrenJobs.length > 0 && (
        <div className='pl-6 flex flex-col gap-2'>
          {childrenJobs?.map((childJob) => (
            <JobItem key={childJob.id} job={childJob} jobs={jobs ?? []} jobTypeMap={jobTypeMap} statusMap={statusMap} />
          ))}
        </div>
      )}
    </Fragment>
  );
};

export default JobList;
